<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { useValidation } from "@/composables/useValidation";
import type { DispatcherDto } from "@/api/dispatchers/dto/dispatcher.dto";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import { useBaseView } from "@/composables";
import PhoneField from "@/components/inputs/PhoneField.vue";
import { phoneExtMask } from "@/constants/mask";
import EmailField from "@/components/inputs/EmailField.vue";

const { t } = useI18n();
const { requiredValidator, selectValidator, phoneExtValidator } = useValidation();
const { roles } = useBaseView();

const model = defineModel<DispatcherDto>({
  default: {
    id: null,
    name: "",
    firstName: "",
    lastName: "",
    roles: [],
    phone: "",
    phoneExt: "",
    timezone: "",
    company: {
      id: 0,
      name: "",
      code: "",
    },
    isSuperAdmin: false,
    allExistingPermissions: [],
  },
});
</script>

<template>
  <v-card class="pa-5">
    <div class="font-size-11 text-grey-darken-1">
      {{ t("general.dispatcher-information") }}
    </div>

    <v-row class="mt-3">
      <v-col>
        <v-text-field
          v-model="model.firstName"
          :label="t('general.first-name')"
          :rules="[requiredValidator]"
          autocomplete="off"
          class="required"
          color="primary"
          density="compact"
          variant="outlined"
        />
      </v-col>

      <v-col>
        <v-text-field
          v-model="model.lastName"
          :label="t('general.last-name')"
          :rules="[requiredValidator]"
          autocomplete="off"
          class="required"
          color="primary"
          density="compact"
          variant="outlined"
        />
      </v-col>

      <v-col>
        <email-field v-model="model.email" :rules="[requiredValidator]" />
      </v-col>

      <v-col>
        <omni-select
          v-model="model.roles"
          :items="roles"
          :label="t('dispatchers.roles')"
          :multiple="true"
          :rules="[selectValidator]"
          item-title="name"
          item-value="id"
          required
        />
      </v-col>

      <v-col>
        <phone-field v-model="model.phone" :label="t('general.phone')" required />
      </v-col>

      <v-col>
        <v-text-field
          v-model="model.phoneExt"
          v-maska="phoneExtMask"
          :label="t('dispatchers.phone-ext')"
          :rules="[phoneExtValidator]"
          autocomplete="off"
          color="primary"
          density="compact"
          variant="outlined"
      /></v-col>
    </v-row>

    <div class="font-size-11 text-grey-darken-1 mt-1">
      {{ t("general.preferences") }}
    </div>

    <v-checkbox v-model="model.emailConfirmed" class="font-size-14 mt-3" color="primary" density="compact" hide-details>
      <template #label>
        <span class="font-size-12 text-grey-darken-2 ml-2">{{ t("general.send-updates") }}</span>
      </template>
    </v-checkbox>
  </v-card>
</template>

<style lang="scss" scoped></style>
